/*****************************************************************
 ** 商家档案 · 经营看板 BFF 服务层
 ** ----------------------------------------------------------------
 ** 配套文档：
 **   docs/商家档案重构与H5看板迁移方案.md（v0.4；其 §9 记录组件库由 arco 回归 antd）
 **   docs/商家档案重构-M1接口契约与登录态审计.md
 **
 ** 说明：
 ** 1. USE_MOCK = false → 打真接口 /athena/board/*（当前处于联调阶段）。
 **    改回 true 即回到纯前端 mock 预览，MOCK.* 数据全部保留在本文里。
 **    ⚠ 本开关只管 board* 系列；下方转引的 GMV / 趋势 / 结算 三个既有
 **    AthenaController 接口永远打真接口，不受本开关控制（评审时注意真假混排）。
 ** 2. 组件侧传参保持扁平（{ tenantId, dateRange } 等），
 **    本层统一包成 workwx 的 { requestData: {...} } 信封。
 **    （$x.post 就是裸 axios.post，不额外包裹，与既有 services/athena.js 同形态）
 ** 3. 响应 shape 以「原型组件实际消费字段」为准，
 **    每个函数的真实来源见函数上方注释的 ← 源接口。
 ** 4. mock 中部分列表同时给了 name/num/value/label 四份冗余字段，
 **    原因是原型内同时存在 Donut(name+num) 与 Bar(value+name) 两种取法，
 **    联调时按后端真实 shape 收敛，删掉多余键。
 ******************************************************************/

import $x from '@/utils/$x';
import {
  athenaGetTenantInfo,
  getBaseTag,
  getCoachDetail,
} from '@/services/athena';

/* GMV / 收入趋势 / 结算金额：直接复用现有 AthenaController 接口（后端无需新增）。
   在此转引一份，是为了让看板各模块只依赖 athenaBoard 一个取数入口，
   便于统一控制 USE_MOCK 与入参信封。 */
export {
  queryGmvData,
  gmvTendencyChart,
  getSettlementData,
} from '@/services/athena';

/** 看板接口总开关：true = 纯前端 mock，false = 打后端（当前：联调中） */
export const USE_MOCK = false;

const ok = (data) => Promise.resolve({ code: '00000', msg: 'ok', data });
const post = (url, params) => $x.post(url, { requestData: params });

/* 列表项补齐 name/num/value/label 四份键，便于多种图表取法 */
const lv = (name, num) => ({ name, num, value: num, label: name });

/* ------------------------------------------------------------------
 * 枚举：H5 侧原本硬编码在 datacenter/index.vue 的 tabs1Data/tabs3Data，
 * 按 M1 §2.0 决议前端静态化，后端不出接口。
 * value 与源接口筛选参数保持一致（dateRange / orderType / userType 都是源接口的 Integer 枚举 id，
 * 其中 orderType 0~5、userType 0/1/2/5，0 表示全部）。
 * ---------------------------------------------------------------- */
export const BOARD_FILTERS = {
  gmvDimension: [
    { value: 'month', label: '按月' },
    { value: 'day', label: '按日' },
  ],
  dateRange: [
    { value: 1, label: '今天' },
    { value: 2, label: '昨天' },
    { value: 3, label: '最近 7 天' },
    { value: 4, label: '最近 30 天' },
    { value: 8, label: '选择日期' },
  ],
  orderType: [
    { value: 0, label: '全部' },
    { value: 1, label: '月卡' },
    { value: 2, label: '年卡' },
    { value: 3, label: 'PRO 或指导会员' },
    { value: 4, label: '指导课' },
    { value: 5, label: '训练计划' },
  ],
  /* value = MemberDataRequest.userType（源接口 Integer），沿用 h5 tabs2Data 的 id：
     0 全部 / 1 月卡 / 2 年卡 / 5 PRO或指导会员 */
  userType: [
    { value: 0, label: '全部会员' },
    { value: 1, label: '月卡' },
    { value: 2, label: '年卡' },
    { value: 5, label: 'PRO 或指导会员' },
  ],
  completionMetric: [
    { value: 'completedNum', label: '完课次数' },
    { value: 'completedUserNum', label: '完课人数' },
    { value: 'completedPerUserNum', label: '人均完课次数' },
  ],
  clockDimension: [
    { value: 'day', label: '按日' },
    { value: 'week', label: '按周' },
    { value: 'month', label: '按月' },
  ],
  funnelRange: [
    { value: 'all', label: '全部' },
    { value: '30d', label: '近 30 日' },
    /* 在期口径：value 必须与后端 MerchantBoardController 的 funnel.put("effective", …) 逐字符一致，
       写错不会报错，只会静默显示空漏斗。首级文案由服务端下发为「在期会员数」。 */
    { value: 'effective', label: '在期会员' },
  ],
};

export function boardFilters() {
  return ok(BOARD_FILTERS);
}

/* ==================================================================
 * 一、经营概览（周报）
 * 源：workwx TenantStoreApi/memberWeeklyOverview
 * ================================================================== */
export function boardWeeklyOverview(params) {
  if (USE_MOCK) return ok(MOCK.weeklyOverview);
  return post('/athena/board/weeklyOverview', params);
}

/* ==================================================================
 * 二、交易经营 / 服务交付 / 会员概览
 * 源：workwx TenantStoreApi/memberData（dataType=1 订单、2 完课）
 *     workwx TenantStoreApi/memberOverview
 *     ⚠ 两接口内层 coachUserId 为空时会分别导致 ifLogin=true / 静默返回空
 * ================================================================== */
export function boardOrderData(params) {
  if (USE_MOCK) return mockOrderData(params);
  return post('/athena/board/orderData', params);
}

export function boardCompletion(params) {
  if (USE_MOCK) return ok(MOCK.completion);
  return post('/athena/board/completion', params);
}

export function boardMemberOverview(params) {
  if (USE_MOCK) return ok(MOCK.memberOverview);
  return post('/athena/board/memberOverview', params);
}

/* ==================================================================
 * 三、看得见活动
 * 源：workwx TenantStoreApi/visibleActivityData（PageRequest）
 *     + memberWeeklyOverview 的三个活动指标（G2：漏斗由 BFF 跳源组装）
 * ================================================================== */
export function boardVisibleActivity(params) {
  if (USE_MOCK) return mockVisibleActivity(params);
  return post('/athena/board/visibleActivity', params);
}

/* ==================================================================
 * 四、会员画像 / 会员打卡
 * 源：metaspace MerchantDataApi getMerchantMemberPortrait
 *                  clockIn 4 个（summary/trend/typeDistribution/habit）
 * 海报 / 照片墙 2026-09-20 抽成独立游标分页接口：统计是一次取数、
 * 图片墙是无限滚动，生命周期不同，合在一起会让拉统计白带一页图。
 * ================================================================== */
export function boardMemberPortrait(params) {
  if (USE_MOCK) return ok(MOCK.memberPortrait);
  return post('/athena/board/memberPortrait', params);
}

export function boardMemberClockIn(params) {
  if (USE_MOCK) return ok(MOCK.memberClockIn);
  return post('/athena/board/memberClockIn', params);
}

/**
 * 会员成果海报（游标分页）← /athena/board/memberStoryPage
 * 源：metaspace pageMemberStory；入参 { tenantId, coachUserId, lastId, limit }
 * 返回 { list, nextLastId, hasMore }；首页 lastId 传 0 或不传（后端默认 15 条/页）。
 */
export function boardMemberStoryPage(params) {
  if (USE_MOCK) return mockCursorPage(MOCK.memberStoryAll, params);
  return post('/athena/board/memberStoryPage', params);
}

/**
 * 打卡照片墙（游标分页）← /athena/board/memberPhotoWallPage
 * 源：metaspace listMerchantMemberFreeSignPhotoWall；入参同海报。
 */
export function boardMemberPhotoWallPage(params) {
  if (USE_MOCK) return mockCursorPage(MOCK.memberPhotoWallAll, params);
  return post('/athena/board/memberPhotoWallPage', params);
}

/* ==================================================================
 * 五、门店运营 + 会员行为（同一 payload 的两个视角，M1 §2.6）
 * 源：metaspace MerchantDataApi store visit 5 个
 *     「今日打卡率」「使用中设备」按 G3/G4 决议先用建议口径
 * ================================================================== */
export function boardStoreVisit(params) {
  if (USE_MOCK) return ok(MOCK.storeVisit);
  return post('/athena/board/storeVisit', params);
}

export function boardStoreDevice(params) {
  if (USE_MOCK) return ok(MOCK.storeDevice);
  return post('/athena/board/storeDevice', params);
}

/** 设备资产盘点明细（服务端分页） */
export function boardStoreDeviceAssets(params) {
  if (USE_MOCK) {
    const list = MOCK.storeDevice.assetList || [];
    const currentPage = params.currentPage || 1;
    const pageSize = params.pageSize || 10;
    const start = (currentPage - 1) * pageSize;
    return Promise.resolve({
      code: '00000',
      msg: 'ok',
      data: {
        total: list.length,
        list: list.slice(start, start + pageSize),
      },
    });
  }
  return post('/athena/board/storeDeviceAssets', params);
}

/* ==================================================================
 * 六、指导课管理
 * 源：merchant TenantRightsApi overview/salesTrend/coachWriteOffTrend/listRights
 * ================================================================== */
export function boardGuidanceCourse(params) {
  if (USE_MOCK) return mockGuidanceCourse(params);
  return post('/athena/board/guidanceCourse', params);
}

/* ==================================================================
 * 七、外壳数据（仅 USE_MOCK 走 mock；真实模式转引现有 athena 接口）
 * ================================================================== */
export function boardTenantInfo(params) {
  if (USE_MOCK) return ok(MOCK.tenantInfo);
  return athenaGetTenantInfo({ requestData: params });
}

export function boardBaseTags(params) {
  if (USE_MOCK) return ok(MOCK.baseTags);
  return getBaseTag({ requestData: params });
}

export function boardCoachList(params) {
  if (USE_MOCK) return ok(MOCK.coachList);
  return getCoachDetail({ requestData: params });
}

/**
 * USE_MOCK 下模拟 /athena/board/guidanceCourse 的服务端行为：
 * 筛选语义与 BFF 一致（expire_7 / no_class_14 / less_5 + search），并回传分页 total。
 * 目的是让组件提前改成「参数下发 + 不本地筛」，切真实接口时零改动。
 */
function mockGuidanceCourse(params) {
  const base = MOCK.guidanceCourse;
  const { courseFilter = 'all', search = '', currentPage = 1, pageSize = 10 } = params || {};
  const today = new Date();
  let rows = (base.courseDetailList || []).slice();

  if (search) {
    rows = rows.filter((r) => `${r.nickName || ''}${r.phone || ''}`.includes(search));
  }
  if (courseFilter === 'expire_7') {
    rows = rows.filter((r) => {
      if (!r.expireDate) return false;
      const days = (new Date(r.expireDate) - today) / 86400000;
      return days >= 0 && days <= 7;
    });
  } else if (courseFilter === 'no_class_14') {
    rows = rows.filter((r) => (r.notClassDays || 0) > 7);
  } else if (courseFilter === 'less_5') {
    rows = rows.filter((r) => (r.remainNum || 0) < 5);
  }

  const total = rows.length;
  const start = (currentPage - 1) * pageSize;
  return ok({
    ...base,
    courseDetailList: rows.slice(start, start + pageSize),
    currentPage,
    pageSize,
    total,
  });
}

/**
 * USE_MOCK 下模拟源接口的口径分支：dateRange=1/2（今天/昨天）不给按日趋势、只给明细，
 * 其余区间反之 —— 保证预览时的分叉行为与真实接口一致。
 */
function mockOrderData(params) {
  const { dateRange } = params || {};
  const base = MOCK.orderData;
  if (dateRange === 1 || dateRange === 2) {
    return ok({
      firstOrderNum: base.firstOrderNum,
      repeatOrderNum: base.repeatOrderNum,
      totalAmountName: base.totalAmountName,
      tendency: [],
      memberList: base.memberList,
    });
  }
  return ok({ ...base, memberList: [] });
}

/**
 * USE_MOCK 下复刻源接口语义：sort=0 按「已体验」降序，非 0 升序；分页并回传 total。
 */
function mockVisibleActivity(params) {
  const base = MOCK.visibleActivity;
  const { sort = 0, currentPage = 1, pageSize = 10 } = params || {};
  const rows = (base.memberList || []).slice();
  rows.sort((a, b) =>
    sort === 0
      ? (b.expedNum || 0) - (a.expedNum || 0)
      : (a.expedNum || 0) - (b.expedNum || 0),
  );
  const start = (currentPage - 1) * pageSize;
  return ok({
    ...base,
    memberList: rows.slice(start, start + pageSize),
    currentPage,
    pageSize,
    total: rows.length,
  });
}

/* ==================================================================
 * mock 数据：字段名 = 前端消费名，数值仅用于评审页面形态
 * ================================================================== */

/** 游标分页单页条数默认值，与后端保持一致 */
const MOCK_PAGE_LIMIT = 15;

/**
 * USE_MOCK 下复刻源 CursorListModel 的游标语义：返回 id 大于 lastId 的下一批，
 * 并回传新游标（id 递增）。真实接口的 lastId 是源端游标，前端只透传不解释。
 */
function mockCursorPage(all, params) {
  const limit = (params && params.limit) || MOCK_PAGE_LIMIT;
  const rawCursor = Number((params && params.lastId) || 0);
  /* 非法游标（null/空串/非数字）一律回退为首页，避免 NaN 让 filter 恒 false 返回空列表 */
  const cursor = Number.isFinite(rawCursor) && rawCursor > 0 ? rawCursor : 0;
  const rest = all.filter((it) => Number(it.id) > cursor);
  const list = rest.slice(0, limit);
  return ok({
    list,
    nextLastId: list.length ? Number(list[list.length - 1].id) : null,
    hasMore: rest.length > list.length,
  });
}

/** 造一批 id 递增的图片行（图片地址仅 mock 预览用，真实接口取源端 picture / imageUrl） */
function buildMockWall(prefix, count, extraOf) {
  const rows = [];
  for (let i = 1; i <= count; i += 1) {
    rows.push({
      id: i,
      userId: `u${i}`,
      imageUrl: `https://picsum.photos/seed/${prefix}${i}/400/400`,
      ...extraOf(i),
    });
  }
  return rows;
}

const MOCK = {
  tenantInfo: {
    tenantId: '10086',
    tenantName: '淑敏大大健身',
    name: '淑敏大大健身',
    ctime: '2023-05-18 14:22:03',
    ifCertification: true,
    encryptTenantId: 'ENC_TENANT_10086',
    coachUserId: '20001',
    encryptCoachUserId: 'ENC_COACH_20001',
    intro: '专注体适能与力量训练的连锁健身工作室，配备 12 名认证教练。',
    location: '上海市徐汇区田林路 397 号',
    longitude: '121.410',
    latitude: '31.183',
    redBookAccountId: 'lefit_power',
    wechatVideoAccountId: '视频号-淑敏大大',
    coachAccountModels: [
      { coachId: '20001', coachNickName: '阿May', coachText: '阿May' },
      { coachId: '20002', coachNickName: 'Derek', coachText: 'Derek' },
    ],
  },
  baseTags: {
    baseTags: [
      { key: '1', title: '企业认证', ifGreen: true },
      { key: '2', title: '连锁品牌', ifGreen: false },
      { key: '3', title: '高完课率', ifGreen: true },
    ],
  },
  coachList: [
    {
      coachId: '20001',
      coachNickName: '阿May',
      coachName: '李某某',
      coachTypeStr: '全职教练',
      encryptUserId: 'ENC_U_20001',
    },
    {
      coachId: '20002',
      coachNickName: 'Derek',
      coachName: '张某某',
      coachTypeStr: '兼职教练',
      encryptUserId: 'ENC_U_20002',
    },
  ],

  weeklyOverview: {
    weekRange: '2026-09-07 - 2026-09-13',
    /* 会员增长：新增 / 流失 + 漏斗（完成首课 / 完成次课）
       源字段 addUserNum / lostUserNum / completeFirstAddUserNum / completeTwoAddUserNum */
    memberGrow: {
      newMemberNum: 12,
      lostMemberNum: 5,
      firstClassDoneNum: 9,
      secondClassDoneNum: 6,
    },
    /* 本周完课分层，源字段 notCompleteLastWeekUserNum 等 */
    weekComplete: { notDoneNum: 23, doneOnceNum: 31, doneTwiceNum: 32 },
    nextWeekExpire: {
      total: 6,
      tip: '完课次数高的会员，更容易转化',
      list: [
        { userId: 'u1', nickName: '小鹿', completedNum: 18, last7Num: 3, memberType: '年卡', tagTone: 'green', expireDate: '2026-09-18' },
        { userId: 'u2', nickName: 'Kevin', completedNum: 6, last7Num: 0, memberType: '月卡', tagTone: 'gold', expireDate: '2026-09-19' },
        { userId: 'u3', nickName: '阿云', completedNum: 12, last7Num: 2, memberType: 'PRO会员', tagTone: 'green', expireDate: '2026-09-20' },
        { userId: 'u4', nickName: 'Momo', completedNum: 3, last7Num: 1, memberType: '年卡', tagTone: 'gold', expireDate: '2026-09-21' },
      ],
    },
  },

  visibleActivity: {
    h5Url: '',
    tips: '数据截至 2026-09-13 23:59',
    /* G2 决议：漏斗源数据在 memberWeeklyOverview（activityUserNum / xplanUserNum /
       xplanFirstUserNum），由 BFF 跳源组装到本接口响应里，前端保持从 visibleActivity 取 */
    funnel: [
      { label: '获得活动奖励', value: 48, unit: ' 人', tip: '奖励发放后 7 天内有效' },
      { label: '当前可体验', value: 35, unit: ' 人', tip: '尚有可用权益' },
      { label: '已体验', value: 18, unit: ' 人', tip: '已完成一次入镜指导' },
    ],
    memberList: [
      { userId: 'u1', nickName: '小鹿', totalRewardNum: 6, canExpNum: 2, expedNum: 4 },
      { userId: 'u2', nickName: 'Kevin', totalRewardNum: 4, canExpNum: 1, expedNum: 3 },
      { userId: 'u3', nickName: '阿云', totalRewardNum: 3, canExpNum: 3, expedNum: 0 },
      { userId: 'u5', nickName: 'Yuki', totalRewardNum: 8, canExpNum: 2, expedNum: 6 },
    ],
  },

  memberOverview: {
    updateTime: '2026-09-14 06:00',
    effectiveUserNum: 86,
    lostUserNum: 42,
    funnel: {
      /* memberType/memberSearchType 真实值由服务端 Apollo 配置（explainConfig.memberSearchType）下发，
         本层不猜：给不确定的阶段置 null 正好用来验「不可点击」降级路径；
         年卡/PRO 两个值与 MemberSearchTypeEnum 一致（4=年卡、5=PRO）。
         effective 为新增的「在期」口径：人群是 effectiveMember（标签非空且不含已到期4/未开通5），
         逐级 ⊂ 上面的 all，首级 num 必须等于 effectiveUserNum；
         四级转化率分母统一取在期会员数（86），不是「上一级」——所以看起来会逐级递减。
         下钻走的是服务端新增的 memberSearchType3：此处把一级置 1（=EFFECTIVE）表示「Apollo 配了」的形态，
         二~五级仍为 null（无对应 code，未配即不可点击）。 */
      all: [
        { stage: '总会员数', num: 128, ringLabel: '在期 86', advice: '可引导续费', memberType: null, memberSearchType: null },
        { stage: '完成训练', num: 96, ringLabel: '75%', advice: '未训练会员需激活', memberType: null, memberSearchType: null },
        { stage: '训练次数≥2', num: 63, ringLabel: '66%', advice: '推 PRO 升级', memberType: null, memberSearchType: null },
        { stage: '年卡人数', num: 28, ringLabel: '44%', advice: '重点维护', memberType: 4, memberSearchType: 4 },
        { stage: 'PRO或指导会员', num: 15, ringLabel: '54%', advice: '高价值人群', memberType: 5, memberSearchType: 5 },
      ],
      '30d': [
        { stage: '总会员数', num: 34, ringLabel: '近30日新增', advice: '', memberType: null, memberSearchType: null },
        { stage: '完成训练', num: 26, ringLabel: '76%', advice: '', memberType: null, memberSearchType: null },
        { stage: '训练次数≥2', num: 17, ringLabel: '65%', advice: '', memberType: null, memberSearchType: null },
        { stage: '年卡人数', num: 8, ringLabel: '47%', advice: '', memberType: 4, memberSearchType: 4 },
        { stage: 'PRO或指导会员', num: 4, ringLabel: '50%', advice: '', memberType: 5, memberSearchType: 5 },
      ],
      effective: [
        /* 一级无 rate（后端 rate 传 null），故 ringLabel 为 null —— 真实形态如此 */
        { stage: '在期会员数', num: 86, ringLabel: null, advice: '可引导续费', memberType: 1, memberSearchType: 1 },
        { stage: '完成训练', num: 64, ringLabel: '74%', advice: '未训练会员需激活', memberType: null, memberSearchType: null },
        { stage: '训练次数≥2', num: 40, ringLabel: '47%', advice: '推 PRO 升级', memberType: null, memberSearchType: null },
        { stage: '年卡人数', num: 20, ringLabel: '23%', advice: '重点维护', memberType: null, memberSearchType: null },
        { stage: 'PRO或指导会员', num: 9, ringLabel: '10%', advice: '高价值人群', memberType: null, memberSearchType: null },
      ],
    },
    distribution: [
      { name: '月卡', num: 41, memberType: 3 },
      { name: '年卡', num: 28, memberType: 4 },
      { name: 'PRO', num: 11, memberType: 5 },
      { name: '自动续费', num: 6, memberType: 21 },
    ],
    weekCompletePie: [
      { name: '未训练', num: 23 },
      { name: '训练1次', num: 31 },
      { name: '训练次数≥2', num: 32 },
    ],
  },

  orderData: {
    firstOrderNum: 18,
    repeatOrderNum: 27,
    totalAmountName: '¥186,420',
    tendency: [
      { date: '09-07', num: 6 },
      { date: '09-08', num: 9 },
      { date: '09-09', num: 7 },
      { date: '09-10', num: 12 },
      { date: '09-11', num: 8 },
      { date: '09-12', num: 11 },
      { date: '09-13', num: 5 },
    ],
    memberList: [
      { userId: 'u1', nickName: '小鹿', avatar: '', phone: '138****2211', amountName: '¥1,299', payTime: '2026-09-14 10:22', orderTypeName: '年卡', channelName: '小程序' },
      { userId: 'u6', nickName: '大壮', avatar: '', phone: '135****7788', amountName: '¥399', payTime: '2026-09-14 08:41', orderTypeName: '月卡', channelName: '企业微信' },
    ],
  },

  completion: {
    summary: { completedNum: 268, completedUserNum: 63, completedPerUserNum: 4.25 },
    dateClassList: [
      { date: '09-07', completedNum: 38, completedUserNum: 21, completedPerUserNum: 1.8 },
      { date: '09-08', completedNum: 42, completedUserNum: 24, completedPerUserNum: 1.75 },
      { date: '09-09', completedNum: 35, completedUserNum: 19, completedPerUserNum: 1.84 },
      { date: '09-10', completedNum: 51, completedUserNum: 27, completedPerUserNum: 1.89 },
      { date: '09-11', completedNum: 44, completedUserNum: 23, completedPerUserNum: 1.91 },
      { date: '09-12', completedNum: 33, completedUserNum: 18, completedPerUserNum: 1.83 },
      { date: '09-13', completedNum: 25, completedUserNum: 14, completedPerUserNum: 1.79 },
    ],
    memberList: [
      { userId: 'u1', nickName: '小鹿', startTime: '09-14 10:00', duration: '62 分钟', className: '力量训练·上肢' },
      { userId: 'u2', nickName: 'Kevin', startTime: '09-14 09:10', duration: '48 分钟', className: '体适能评估' },
    ],
  },

  guidanceCourse: {
    summary: { expireIn7DaysNum: 4, remainCourseNum: 132, totalSoldNum: 480, totalUsedNum: 347 },
    currentPage: 1,
    pageSize: 10,
    total: 3,
    sellUsedTrend: {
      labels: ['第1周', '第2周', '第3周', '第4周', '第5周', '第6周'],
      sold: [22, 30, 18, 26, 34, 28],
      used: [18, 24, 20, 22, 29, 25],
    },
    courseUsedTrend: {
      labels: ['第1周', '第2周', '第3周', '第4周', '第5周', '第6周'],
      series: [
        { name: '阿May', data: [8, 10, 7, 9, 12, 10] },
        { name: 'Derek', data: [6, 7, 9, 8, 10, 8] },
      ],
    },
    courseDetailList: [
      { userId: 'u1', nickName: '小鹿', phone: '138****2211', remainNum: 2, notClassDays: 12, lastClassStartDate: '2026-08-30', expireDate: '2026-09-18' },
      { userId: 'u3', nickName: '阿云', phone: '139****8802', remainNum: 8, notClassDays: 3, lastClassStartDate: '2026-09-10', expireDate: '2026-11-02' },
      { userId: 'u5', nickName: 'Yuki', phone: '137****5544', remainNum: 4, notClassDays: 9, lastClassStartDate: '2026-09-03', expireDate: '2026-10-12' },
    ],
  },

  memberPortrait: {
    summary: { totalMemberNum: 128, monthNewMemberNum: 21 },
    painWordCloud: [
      { name: '腰腹松弛', weight: 42 },
      { name: '肩颈酸痛', weight: 35 },
      { name: '体脂偏高', weight: 30 },
      { name: '膝盖不适', weight: 22 },
      { name: '作息不规律', weight: 18 },
      { name: '缺乏动力', weight: 14 },
    ],
    /* 源 memberTypes：VO 已透出，mock 同步给值，避免预览永远不经过这个字段 */
    memberTypeWordCloud: [
      { name: '月卡', weight: 41 },
      { name: '年卡', weight: 28 },
      { name: 'PRO', weight: 11 },
      { name: '自动续费', weight: 6 },
    ],
    genderDistribution: [lv('女', 78), lv('男', 50)],
    ageDistribution: [lv('18-25', 24), lv('26-30', 38), lv('31-35', 33), lv('36-40', 19), lv('40+', 14)],
    purchasingPower: [lv('高', 22), lv('中高', 41), lv('中', 45), lv('低', 20)],
    regionDistribution: [lv('徐汇区', 46), lv('长宁区', 28), lv('闵行区', 22), lv('黄浦区', 16), lv('其他', 16)],
  },

  memberClockIn: {
    summary: {
      totalClockUserNum: 96,
      weekClockUserNum: 38,
      monthClockUserNum: 62,
      weekPerUserTimes: 3.4,
    },
    trend: {
      granularity: 'day',
      labels: ['09-07', '09-08', '09-09', '09-10', '09-11', '09-12', '09-13'],
      total: [42, 51, 38, 60, 55, 33, 28],
      memberCount: [30, 34, 28, 40, 37, 22, 19],
      recordCount: [42, 51, 38, 60, 55, 33, 28],
    },
    typeDistribution: [lv('训练打卡', 128), lv('饮食打卡', 64), lv('体重打卡', 37), lv('其他', 12)],
    habitLevelDistribution: [lv('高度自律', 18), lv('较规律', 34), lv('偶发', 30), lv('已中断', 14)],
    /* 源 HabitLevelDistributionModel.introduction，mock 同步给值 */
    habitIntroduction: '按近 30 天打卡频率与连续性划分，仅供经营参考',
  },

  /* 会员成果海报 / 打卡照片墙（已抽成独立游标分页接口，不再挂在上面两个聚合对象里） */
  memberStoryAll: buildMockWall('p', 20, (i) => ({
    storyScene: i % 2 ? '首课完成' : '体重里程碑',
    completeTime: 1757721600000 + i * 86400000,
    tag: (i % 2) + 1,
  })),
  memberPhotoWallAll: buildMockWall('w', 18, () => ({})),

  storeVisit: {
    kpi: {
      todayVisitNum: 34,
      todayVisitHint: '较昨日 +8%',
      todayOnlineClockNum: 21,
      todayOnlineClockHint: '较昨日 -5%',
      monthVisitUserNum: 186,
      monthVisitUserHint: '较上月 +12%',
      monthOnlineClockNum: 97,
      monthOnlineClockHint: '较上月 +4%',
    },
    realtime: {
      /* 源 peakHour 是整点字符串（如 "14"，将来可能变 "14-15"） */
      peakPeriod: '19',
      peakTip: '今日高峰时段 19:00-20:00',
      /* G3 已按官方口径定为：今日进店会员中同时线上打卡的占比（分母是 inStoreMemberCount） */
      clockRate: '38.2%',
      clockRateHint: '今日进店会员 34 人，其中 13 人同时线上打卡',
    },
    periodDistribution: [
      { name: '06:00', value: 3 }, { name: '08:00', value: 5 },
      { name: '10:00', value: 4 }, { name: '12:00', value: 6 },
      { name: '14:00', value: 2 }, { name: '16:00', value: 5 },
      { name: '18:00', value: 12 }, { name: '20:00', value: 9 },
      { name: '22:00', value: 3 },
    ],
    todayUsers: [
      /* monthVisitNum 源端恒 0 占位，mock 不再放假值（UI 已改读 monthVisitDays） */
      {
        userId: 'u1',
        nickName: '小鹿',
        timeline: '19:02 到店 → 19:14 力量区 → 20:06 离场',
        monthVisitDays: 14,
      },
      {
        userId: 'u2',
        nickName: 'Kevin',
        timeline: '20:11 到店 → 20:15 有氧区 → 21:02 离场',
        monthVisitDays: 6,
      },
    ],
    activeTrend: {
      labels: ['09-07', '09-08', '09-09', '09-10', '09-11', '09-12', '09-13'],
      visit: [28, 31, 26, 35, 33, 19, 16],
      onlineOnly: [9, 12, 8, 14, 11, 7, 6],
    },
    frequencyLevel: [lv('高（≥12次/月）', 21), lv('中（6-11次）', 38), lv('低（1-5次）', 47), lv('未到店', 22)],
    activeTop: {
      monthVisit: [
        { userId: 'u1', nickName: '小鹿', visitDays: 14 },
        { userId: 'u3', nickName: '阿云', visitDays: 11 },
        { userId: 'u5', nickName: 'Yuki', visitDays: 9 },
        { userId: 'u6', nickName: '大壮', visitDays: 7 },
      ],
      weekVisit: [
        { userId: 'u1', nickName: '小鹿', visitDays: 5 },
        { userId: 'u3', nickName: '阿云', visitDays: 4 },
        { userId: 'u6', nickName: '大壮', visitDays: 2 },
      ],
      monthOnlineClock: [
        { userId: 'u2', nickName: 'Kevin', clockDays: 10 },
        { userId: 'u1', nickName: '小鹿', clockDays: 8 },
        { userId: 'u7', nickName: 'Nina', clockDays: 6 },
      ],
      weekOnlineClock: [
        { userId: 'u2', nickName: 'Kevin', clockDays: 6 },
        { userId: 'u1', nickName: '小鹿', clockDays: 5 },
        { userId: 'u7', nickName: 'Nina', clockDays: 3 },
      ],
    },
    clockBehavior: [
      { name: '仅训练', num: 46, value: 46, label: '仅训练' },
      { name: '训练+体测', num: 12, value: 12, label: '训练+体测' },
      { name: '训练+打卡', num: 21, value: 21, label: '训练+打卡' },
      { name: '到店未训练', num: 8, value: 8, label: '到店未训练' },
    ],
  },

  storeDevice: {
    tips: '近 30 天碰一碰数据',
    asset: {
      deviceTotal: 86,
      deviceTotalHint: '已录入设备总数',
      /* G4：「使用中设备」口径待 metaspace owner 确认 */
      deviceActive: 61,
      deviceActiveHint: '近 7 天有使用记录',
    },
    hotTop6: [
      { name: '史密斯机', value: 248 },
      { name: '坐姿推胸器', value: 201 },
      { name: '高位下拉', value: 178 },
      { name: '划船机', value: 142 },
      { name: '腿举机', value: 118 },
      { name: '龙门架', value: 96 },
    ],
    hourUsage: {
      labels: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22],
      value: [4, 6, 3, 2, 8, 5, 6, 9, 12, 18, 27, 32, 26, 14, 6],
    },
  },
};
